import { useCallback, useEffect, useState } from 'react';
import {
  homeLiquidity,
  homePortfolio,
  homeStake,
  homeSwap,
  homeLoan,
  homeLock,
  homeFiat,
} from '../../assets/icons';
import { chartBall } from '../../assets/images';
import Card from '../../components/elements/Card';
import StakingStatCard from '../Stake/components/StakingStatsCard';
import Header from './components/Header';
import HomeStatCard from './components/HomeStatCard';
import HomeCard from './widgets/HomeCard';
import { useLanguage, useLanguageActions } from '@hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { circulationSupply, getTotalHolders, getTradingVolume } from './api';
import { TIFI_ADDRESS, RPC_URL } from '@config/constants';
import { ethers } from 'ethers';
import { useWeb3React } from '@web3-react/core';
import { minABI } from '@config/TiFi_min_abi';
import { getStakApy, getTvl } from '@features/Stake/api';
import { convertNumber, toFixed } from '@utils/tifi';
import WalletConnector from '@components/widget/connect-wallet/wallet-connector';
import { tifiTokenAbi } from '@config/Tifi_token_abi';
import tifiRoutes from '@components/router/tifiRoutes';
import { T } from 'react-translator-component';

const Home = () => {
  const { language } = useLanguage();
  const { setLanguage } = useLanguageActions();
  const navigate = useNavigate();
  const [balance, setBalance] = useState<number>(0);
  const [totalBurn, setTotalBurn] = useState<number>(0);
  const { account: address, library: provider } = useWeb3React();

  // Fetch Chart Data
  const { data: apy } = useQuery(['apy-data'], getStakApy);

  // Get Balance
  const getBalance = useCallback(async () => {
    const signer = provider.getSigner();
    let contract = new ethers.Contract(TIFI_ADDRESS, minABI, signer);
    try {
      if (address != null) {
        const tifiBal = await contract.balanceOf(address);
        setBalance(Math.floor(Number(tifiBal._hex) / Number(10 ** 18))); // TiFi Decimal is 18
        return Math.floor(Number(tifiBal._hex) / Number(10 ** 18));
      } else {
        return 0;
      }
    } catch (error) {
      return 0;
    }
  }, [address, provider]);

  const getTotalSupply = useCallback(async () => {
    const signer = provider ? provider.getSigner() : new ethers.providers.JsonRpcProvider(RPC_URL);
    let contract = new ethers.Contract(TIFI_ADDRESS, tifiTokenAbi, signer);
    const total = await contract.totalBurn();
    setTotalBurn(Math.floor(Number(total._hex) / Number(10 ** 18)));
    return Math.floor(Number(total._hex) / Number(10 ** 18));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, provider]);

  const { data: tifiCirculationSupply, isLoading } = useQuery(
    ['tifi-circulation-supply'],
    circulationSupply,
  );
  const { data: tvl, isLoading: isTvlLoading } = useQuery(['tvl-data'], getTvl);
  const { data: holders } = useQuery(['hold-data'], getTotalHolders);
  const { data: marketData } = useQuery(['market-data'], getTradingVolume);

  useEffect(() => {
    setLanguage({ language: language });
  }, [language, setLanguage]);

  useEffect(() => {
    if (address && provider) {
      getBalance();
    }
    getTotalSupply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, provider]);

  const processMarketData = () => {
    if (marketData) {
      return `$${convertNumber(marketData)}`;
    }
    return T('loading ...');
  }
  const stats = [
    {
      id: 1,
      title: 'Your TIFI Balance',
      value: `${convertNumber(balance)}`,
    },
    {
      id: 2,
      title: 'TVL',
      value: `$${tvl?.total_tvl && toFixed(tvl?.total_tvl / 1000000, 4)}M`,
    },
    {
      id: 3,
      title: 'Trading Vol. Last 24 Hours',
      value: processMarketData(),
    }
  ];

  return (
    <section className="py-8 relative">
      <div className="absolute -z-30 top-8 lg:right-0 lg:top-0 lg:opacity-100 opacity-20">
        <img src={chartBall} alt="ChatBall" />
      </div>
      <div className="static z-10">
        <Header
          title="Welcome to TiFi Bank!"
          description="The First Decentralized Bank for Allverse"
        />
      </div>
      {/* Wallet is Connected Render Stats*/}
      {!address && (
        <div className="lg:w-96 my-8 w-full">
          <WalletConnector label="Connect Wallet" />
        </div>
      )}
      <section className="my-6">
        <Card className="rounded-lg bg-gradient-to-r from-tifi-dark via-tifi-dark to-[#0027374a] bg-opacity-30 backdrop-blur-xl">
          <section className="grid lg:grid-cols-3 grid-cols-1 gap-4">
            {stats.length > 0 &&
              stats.map((st, idx) => (
                <div
                  key={st.id}
                  className={`${stats.length - 1 !== idx &&
                    'lg:border-r lg:border-r-light-60'
                    } lg: pl - 4`}
                >

                  <StakingStatCard
                    loading={isLoading || isTvlLoading}
                    title={st.title}
                    desc={idx === 0 && !address ? T('N/A') : st?.value as string}
                  />
                </div>
              ))}
          </section>
        </Card>
      </section>

      <section className="">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <HomeCard
            icon={homeFiat}
            btnLabel="BUY WITH BANK CARDS"
            title="Visa & Mastercard"
            description="Buy TiFi Token with Visa and Mastercard!"
            onClick={() => window.open(tifiRoutes.FIAT, '_blank')}
          />
          <HomeCard
            icon={homeSwap}
            btnLabel="GO TO SWAP"
            title="Swap"
            description="Exchange Cryptos with other Cryptos"
            onClick={() => navigate(tifiRoutes.SWAP)}
          />
          <HomeCard
            apy={true}
            apyValue={
              !apy || apy?.length === 0
                ? 0
                : toFixed(apy[apy?.length - 1].tifiApy, 4)
            }
            icon={homeStake}
            btnLabel="GO TO STAKE"
            title="Stake"
            description="Stake TIFI to earn TIFI and WBNB with high APY"
            onClick={() => navigate(tifiRoutes.STAKE)}
          />
          <HomeCard
            icon={homeLock}
            btnLabel="START EARNING"
            title="Lock To Earn"
            description="Lock TiFi Token with fix period for high return"
            onClick={() => navigate(tifiRoutes.STAKE_LOCK)}
          />
          <HomeCard
            icon={homeLoan}
            btnLabel="GO TO CRYPTO LOAN"
            title="Crypto Loan"
            description="Supply crypto assets to earn interests and get fast loan without credit check."
            onClick={() => navigate(tifiRoutes.LOAN)}
          />
          <HomeCard
            icon={homeLiquidity}
            btnLabel="GO TO LIQUIDITY"
            title="Liquidity"
            description="Enjoy Earning by Providing Liquidity"
            onClick={() => navigate(tifiRoutes.LIQUIDITY)}
          />
          <HomeCard
            icon={homePortfolio}
            btnLabel="GO TO PLAY"
            title="Win"
            description="Buy mystery shopping bags and win TIFI!"
            onClick={() => navigate(tifiRoutes.LOTTERY)}
          />
        </div>
        <section className="mt-6">
          <Card className="rounded-lg bg-gradient-to-r from-tifi-dark to-transparent">
            <section className="grid lg:grid-cols-3 grid-cols-2 gap-4">
              <div>
                <HomeStatCard title="Circulating Supply" desc={tifiCirculationSupply ? `${convertNumber(tifiCirculationSupply)} TIFI` : T('Loading ...')} />
              </div>
              <div>
                <HomeStatCard
                  title={`TIFI ${T('Burnt')}`}
                  desc={`${convertNumber(totalBurn)} ` || T('Loading...')}
                />
              </div>
              <div>
                <HomeStatCard title={`TIFI ${T('Holders')}`} desc={holders || T('Loading ...')} />
              </div>
            </section>
          </Card>
        </section>
      </section>
    </section>
  );
};

export default Home;
